// // src/pages/letterhead/utils/buildTemplatePayload.ts

// import { TemplateFormState, CreateTemplatePayload } from "../types/letterhead.types.ts";

// export const buildTemplatePayload = (state: TemplateFormState, cid: string, idType: 'company_id' | 'user_id' = 'company_id') => {
//   const components = [
//     {
//       type: "header", displayOrder: 1,
//       properties: {
//         backgroundColor: state.headerColor, showBackground: String(state.showHeaderBg),
//         showCompanyInfo: String(state.showCompanyInfo), logoAlignment: state.logoAlignment,
//         textAlignment: state.textAlignment, logoSize: state.logoSize, headerLayout: state.headerLayout,
//         logoUrl: state.logoFile ? "" : state.logoUrl,
//         visibleFields: JSON.stringify(state.showCompanyFields),

//         //  Sends the sorted header order to the database
//         headerFieldOrder: JSON.stringify(state.headerFieldOrder),
//       },
//       identityFields: [
//         { key: "companyName", value: state.companyName }, { key: "companyEmail", value: state.companyEmail },
//         { key: "companyPhone", value: state.companyPhone }, { key: "companyAddress", value: state.companyAddress },
//         { key: "cin", value: state.cin }, { key: "gst", value: state.gst },
//         ...(state.customFields || []).map(f => ({ key: f.key, value: f.value }))
//       ],
//     },
//     {
//       type: "body", displayOrder: 2,
//       properties: { decorativeLines: String(state.decorativeLines), lineWidth: state.lineWidth, lineColor: state.lineColor }
//     },
//     {
//       type: "footer", displayOrder: 3,
//       properties: {
//         backgroundColor: state.footerColor, text: state.footerText, showCompanyInfo: String(state.showCompanyInfoFooter),
//         visibleFields: JSON.stringify(state.showFooterFields),

//         //  Sends the sorted footer order to the database
//         footerFieldOrder: JSON.stringify(state.footerFieldOrder),

//         enableQR: String(state.enableQR),
//         qrData: state.enableQR ? JSON.stringify({ iqCode: state.iqCode, issuedBy: state.issuedBy, issuedTo: state.issuedTo, subject: state.subject }) : undefined,
//       }
//     },
//   ];

//   const idField = idType === 'user_id' ? { company_id: cid } : { company_id: cid };
//   // const idField =  { company_id: cid };
//   const payload: CreateTemplatePayload = { ...idField, templateName: state.templateName, status: "active", components };
//   const formData = new FormData();
//   formData.append("payload", JSON.stringify(payload));
//   if (state.logoFile) formData.append("logoFile", state.logoFile);

//   return formData;
// };

// src/pages/letterhead/utils/buildTemplatePayload.ts

import { TemplateFormState, CreateTemplatePayload } from "../types/letterhead.types.ts";

/**
 * Builds the template payload for API submission
 * @param state - The template form state
 * @param cid - Company ID or User ID
 * @param idType - Type of ID being used ('company_id' or 'user_id')
 * @returns FormData object ready for API submission
 */
export const buildTemplatePayload = (
  state: TemplateFormState, 
  cid: string, 
  idType: 'company_id' | 'user_id' = 'company_id'
): FormData => {
  
  // ============================================
  // 1. BUILD HEADER COMPONENT
  // ============================================
  const headerComponent = {
    type: "header", 
    displayOrder: 1,
    properties: {
      backgroundColor: state.headerColor || '#ffffff',
      showBackground: String(state.showHeaderBg ?? true),
      showCompanyInfo: String(state.showCompanyInfo ?? true),
      logoAlignment: state.logoAlignment || 'left',
      textAlignment: state.textAlignment || 'left',
      logoSize: state.logoSize || 'medium',
      headerLayout: state.headerLayout || 'stacked',
      // Only send logoUrl if no logoFile is being uploaded
      logoUrl: state.logoFile ? "" : (state.logoUrl || ""),
      visibleFields: JSON.stringify(state.showCompanyFields || {}),
      headerFieldOrder: JSON.stringify(state.headerFieldOrder || []),
    },
    identityFields: [
      { key: "companyName", value: state.companyName || "" },
      { key: "companyEmail", value: state.companyEmail || "" },
      { key: "companyPhone", value: state.companyPhone || "" },
      { key: "companyAddress", value: state.companyAddress || "" },
      { key: "cin", value: state.cin || "" },
      { key: "gst", value: state.gst || "" },
      ...(state.customFields || []).map(f => ({ 
        key: f.key, 
        value: f.value || "" 
      }))
    ],
  };

  // ============================================
  // 2. BUILD BODY COMPONENT
  // ============================================
  const bodyComponent = {
    type: "body", 
    displayOrder: 2,
    properties: {
      decorativeLines: String(state.decorativeLines || 1),
      lineWidth: state.lineWidth || 'full',
      lineColor: state.lineColor || '#6B7280',
    }
  };

  // ============================================
  // 3. BUILD FOOTER COMPONENT
  // ============================================
  const footerProperties: any = {
    backgroundColor: state.footerColor || '#ffffff',
    text: state.footerText || '',
    showCompanyInfo: String(state.showCompanyInfoFooter ?? false),
    visibleFields: JSON.stringify(state.showFooterFields || {}),
    footerFieldOrder: JSON.stringify(state.footerFieldOrder || []),
    enableQR: String(state.enableQR ?? false),
  };

  // Only add qrData if QR is enabled
  if (state.enableQR) {
    footerProperties.qrData = JSON.stringify({ 
      iqCode: state.iqCode || '', 
      issuedBy: state.issuedBy || '', 
      issuedTo: state.issuedTo || '', 
      subject: state.subject || '' 
    });
  }

  const footerComponent = {
    type: "footer", 
    displayOrder: 3,
    properties: footerProperties,
  };

  // ============================================
  // 4. BUILD COMPONENTS ARRAY
  // ============================================
  const components = [headerComponent, bodyComponent, footerComponent];

  // ============================================
  // 5. BUILD PAYLOAD
  // ============================================
  const idField = idType === 'user_id' 
    ? { company_id: cid } 
    : { company_id: cid };

  const payload: CreateTemplatePayload = {
    ...idField,
    templateName: state.templateName || 'Untitled Template',
    status: "active",
    components,
  };

  // ============================================
  // 6. BUILD FORM DATA
  // ============================================
  const formData = new FormData();
  formData.append("data", JSON.stringify(payload));
  
  // Append logo file if it exists
  if (state.logoFile) {
    formData.append("logoFile", state.logoFile);
  }

  // Debug log - Remove in production
  console.log('📝 Template Payload:', JSON.stringify(payload, null, 2));
  console.log('📎 Logo File:', state.logoFile ? state.logoFile.name : 'No file');

  return formData;
};

// ============================================
// 7. ADDITIONAL EXPORTS (if needed)
// ============================================

/**
 * Helper function to validate template state before submission
 */
export const validateTemplateState = (state: TemplateFormState): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!state.templateName || state.templateName.trim() === '') {
    errors.push('Template name is required');
  }

  if (!state.companyName || state.companyName.trim() === '') {
    errors.push('Company name is required');
  }

  if (!state.companyEmail || state.companyEmail.trim() === '') {
    errors.push('Company email is required');
  }

  // Validate email format
  if (state.companyEmail && !isValidEmail(state.companyEmail)) {
    errors.push('Invalid company email format');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Helper function to validate email format
 */
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Helper function to clean template state before submission
 */
export const cleanTemplateState = (state: TemplateFormState): TemplateFormState => {
  const cleanedState = { ...state };

  // Remove empty custom fields
  if (cleanedState.customFields) {
    cleanedState.customFields = cleanedState.customFields.filter(
      field => field.key && field.key.trim() !== ''
    );
  }

  // Ensure boolean values are properly set
  cleanedState.showHeaderBg = cleanedState.showHeaderBg ?? true;
  cleanedState.showCompanyInfo = cleanedState.showCompanyInfo ?? true;
  cleanedState.showCompanyInfoFooter = cleanedState.showCompanyInfoFooter ?? false;
  cleanedState.enableQR = cleanedState.enableQR ?? false;

  // Ensure arrays are defined
  cleanedState.showCompanyFields = cleanedState.showCompanyFields || [];
  cleanedState.showFooterFields = cleanedState.showFooterFields || [];
  cleanedState.headerFieldOrder = cleanedState.headerFieldOrder || [];
  cleanedState.footerFieldOrder = cleanedState.footerFieldOrder || [];

  return cleanedState;
};

// ============================================
// 8. DEFAULT EXPORT (if needed)
// ============================================
export default buildTemplatePayload;